import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { ProjectElementNodeWithDrawingDesignation } from 'api/graphQL/types/projectElement.types'
import { useEffect } from 'react'
import { useFamilyAddedProductTypesQuery } from '../api/useFamilyAddedProductTypesQuery'
import { useShallowSelector } from '../Workspace2Store'

/**
 * Properties for the useFamilyAddedProductTypes hook.
 */
interface UseFamilyAddedProductTypesProps {
    selectedProjectElementNode?: Partial<ProjectElementNode> | null
    projectId: string
    setFamilyAddedProductTypes?: (
        familyAddedProductTypes: ProjectElementNodeWithDrawingDesignation[]
    ) => void
}

/**
 * Custom hook to manage fetching family added product types based on the selected project element node.
 * @param {string} param0.projectId - The ID of the project for which to fetch family added product types.
 * @param {Function} param0.setFamilyAddedProductTypes - A callback function to set the family added product types in the state.
 * @returns {Object} An object containing the `fetchFamilyAddedProductTypes` function to trigger the data fetch.
 */
export const useFamilyAddedProductTypes = ({
    selectedProjectElementNode,
    projectId,
}: UseFamilyAddedProductTypesProps) => {
    const {
        setSelectedProjectElementNode,
        setFamilyAddedProductTypes,
    } = useShallowSelector((s) => ({
        setSelectedProjectElementNode: s.actions.setSelectedProjectElementNode,
        setFamilyAddedProductTypes: s.actions.setFamilyAddedProductTypes,
    }))
    const { fetchFamilyAddedProductTypes } = useFamilyAddedProductTypesQuery({
        projectId,
        setFamilyAddedProductTypes,
    })

    useEffect(() => {
        const node = selectedProjectElementNode
        if (!node) return

        const isProductFamily =
            node?.elementType === ProjectElementType.PRODUCTFAMILY
        const hasRequiredIds = node?.elementId && node?.id

        if (isProductFamily && hasRequiredIds) {
            fetchFamilyAddedProductTypes(
                selectedProjectElementNode.elementId ?? '',
                selectedProjectElementNode.id ?? ''
            )
        } else {
            setFamilyAddedProductTypes?.([])
        }
        setSelectedProjectElementNode(selectedProjectElementNode)
    }, [
        selectedProjectElementNode,
        projectId,
        fetchFamilyAddedProductTypes,
        setFamilyAddedProductTypes,
        setSelectedProjectElementNode,
    ])

    return {
        fetchFamilyAddedProductTypes,
    }
}
